import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AdminService } from "Services/adminService";

interface Transaction {
  _id: string;
  method: string;
  currency: string;
  status: string;
  user?: {
    _id: string;
    name: string;
    last_name?: string;
    email: string;
    registrationNumber?: string;
  };
  exam?: {
    _id: string;
    examName: string;
  };
  mappingId?: string;
  registerationNumber?: string;
  orderId?: string;
  paymentId?: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
}

const statusVariant = (status: string) => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "expired":
      return "secondary";
    case "failed":
      return "danger";
    default:
      return "light";
  }
};

const Field = ({ label, value }: { label: string; value: any }) => (
  <Col md={6} className="mb-3">
    <div className="text-muted fs-12 mb-1">{label}</div>
    <div className="fs-14 fw-500 text-break">{value || "-"}</div>
  </Col>
);

export default function TransactionDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(false);

  const getTransaction = async () => {
    if (!params.transactionId) return;
    setLoading(true);
    try {
      const res = await AdminService.getTransactionById(params.transactionId);
      if (res?.status === 200) {
        setTransaction(res.data.data?.transaction || res.data.data || res.data.transaction);
      }
    } catch (error) {
      console.error("Error fetching transaction:", error);
      toast.error("Failed to fetch transaction details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTransaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.transactionId]);

  return (
    <Container fluid>
      <div className="py-4">
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon
            icon={faArrowLeft}
            className="cursor-pointer me-2"
            role="button"
            onClick={() => navigate(-1)}
          />
          <h5 className="mb-0">Transaction Details</h5>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        ) : !transaction ? (
          <Card>
            <Card.Body className="text-center py-4">Transaction not found</Card.Body>
          </Card>
        ) : (
          <>
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="fw-600">Payment</span>
                <Badge bg={statusVariant(transaction.status)} className="text-capitalize">
                  {transaction.status}
                </Badge>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Field
                    label="Amount"
                    value={
                      transaction.amount != null
                        ? `${transaction.currency || ""} ${transaction.amount}`.trim()
                        : "-"
                    }
                  />
                  <Field label="Method" value={transaction.method} />
                  <Field label="Order ID" value={transaction.orderId} />
                  <Field label="Payment ID" value={transaction.paymentId} />
                  <Field
                    label="Created At"
                    value={
                      transaction.createdAt
                        ? new Date(transaction.createdAt).toLocaleString()
                        : "-"
                    }
                  />
                  <Field
                    label="Updated At"
                    value={
                      transaction.updatedAt
                        ? new Date(transaction.updatedAt).toLocaleString()
                        : "-"
                    }
                  />
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header className="fw-600">User</Card.Header>
              <Card.Body>
                <Row>
                  <Field
                    label="Name"
                    value={`${transaction.user?.name || ""} ${transaction.user?.last_name || ""}`.trim()}
                  />
                  <Field label="Email" value={transaction.user?.email} />
                  <Field
                    label="Registration No."
                    value={
                      transaction.user?.registrationNumber || transaction.registerationNumber
                    }
                  />
                </Row>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header className="fw-600">Exam</Card.Header>
              <Card.Body>
                <Row>
                  <Field label="Exam Name" value={transaction.exam?.examName} />
                  <Field label="Mapping ID" value={transaction.mappingId} />
                </Row>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </Container>
  );
}
